import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Search, Save, Loader2, PlayCircle, AlertTriangle, CheckCircle2,
  Shield, ExternalLink, Copy, RefreshCw, Eye, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const MASK = '••••••••';

/**
 * SuperAdmin → GSTIN Providers.
 * Pick an active primary, order fallbacks, drop keys per provider,
 * set daily budget, and hit "Test lookup" to validate before go-live.
 */
export function AdminGstinProvidersPage() {
  const { admin, loading } = useAdminAuth();
  const [cfg, setCfg] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});   // { [provider]: bool }
  const [testOut, setTestOut] = useState({});   // { [provider]: {ok, ...} }
  const [testGstin, setTestGstin] = useState('27AABCU9603R1ZK');
  const [showSecret, setShowSecret] = useState({}); // { [provider:key]: bool }

  const refresh = useCallback(async () => {
    setFetching(true);
    try {
      const r = await adminApi.get('/admin/gstin-providers');
      setCfg(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load config');
    } finally { setFetching(false); }
  }, []);

  useEffect(() => { if (admin) refresh(); }, [admin, refresh]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;
  if (admin.role !== 'superadmin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100 p-6">
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 max-w-md text-center">
          <Shield className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h2 className="font-semibold mb-2">SuperAdmin only</h2>
          <p className="text-sm text-slate-400">The GSTIN provider stack holds raw vendor credentials — only SuperAdmins can view or edit it.</p>
        </div>
      </div>
    );
  }

  const patchProvider = (key, update) => {
    setCfg((prev) => ({
      ...prev,
      providers: { ...prev.providers, [key]: { ...prev.providers[key], ...update } },
    }));
  };
  const patchKey = (provider, fieldKey, value) => {
    setCfg((prev) => ({
      ...prev,
      providers: {
        ...prev.providers,
        [provider]: {
          ...prev.providers[provider],
          keys: { ...(prev.providers[provider].keys || {}), [fieldKey]: value },
        },
      },
    }));
  };

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      // Build payload — strip masked secrets (backend treats mask as "keep").
      const providers = {};
      for (const [k, p] of Object.entries(cfg.providers)) {
        providers[k] = {
          enabled: !!p.enabled,
          daily_budget: Number(p.daily_budget) || 0,
          keys: p.keys || {},
        };
      }
      const r = await adminApi.put('/admin/gstin-providers', {
        primary: cfg.primary,
        fallback_order: cfg.fallback_order,
        providers,
      });
      setCfg(r.data);
      toast.success('GSTIN providers saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const runTest = async (provider) => {
    setTesting((t) => ({ ...t, [provider]: true }));
    setTestOut((o) => ({ ...o, [provider]: null }));
    try {
      const r = await adminApi.post('/admin/gstin-providers/test', {
        provider, gstin: testGstin,
      });
      setTestOut((o) => ({ ...o, [provider]: r.data }));
      if (r.data.ok) toast.success(`${provider}: OK`);
      else toast.error(`${provider}: ${r.data.error}`);
    } catch (err) {
      setTestOut((o) => ({
        ...o, [provider]: { ok: false, error: err?.response?.data?.detail || 'Test failed' },
      }));
    } finally { setTesting((t) => ({ ...t, [provider]: false })); }
  };

  const moveFallback = (idx, dir) => {
    setCfg((prev) => {
      const arr = [...prev.fallback_order];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...prev, fallback_order: arr };
    });
  };

  return (
    <div
      data-testid="admin-gstin-providers-page"
      className="min-h-screen"
      style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/admin" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800">
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <Search className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold">GSTIN Fetch Providers</div>
            <div className="text-xs text-slate-400">Pick the active primary + fallback order, drop vendor keys, test before go-live.</div>
          </div>
          <Button onClick={refresh} disabled={fetching} variant="outline" size="sm"
            className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${fetching ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={save} disabled={saving || !cfg}
            data-testid="gstin-providers-save"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Save
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {!cfg || fetching ? (
          <div className="p-10 flex items-center justify-center text-slate-400 gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Priority card */}
            <section className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6">
              <h2 className="text-white font-semibold mb-1">Request Routing</h2>
              <p className="text-xs text-slate-400 mb-4">
                Primary is tried first. If it returns a transient failure (403, 429, 5xx) the next enabled fallback is tried silently.
                A 404 "GSTIN not found" never falls over — it's authoritative.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Primary provider</Label>
                  <select
                    data-testid="gstin-primary-select"
                    value={cfg.primary}
                    onChange={(e) => setCfg({ ...cfg, primary: e.target.value, fallback_order: cfg.fallback_order.filter((p) => p !== e.target.value) })}
                    className="w-full mt-1.5 bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2 text-sm"
                  >
                    {Object.entries(cfg.providers).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Fallback order (top → bottom)</Label>
                  <ul className="mt-1.5 space-y-1.5" data-testid="gstin-fallback-list">
                    {cfg.fallback_order.map((pk, idx) => (
                      <li key={pk} className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-md px-3 py-1.5">
                        <span className="text-sm text-slate-200 flex-1">{idx + 1}. {cfg.providers[pk]?.label || pk}</span>
                        <button onClick={() => moveFallback(idx, -1)} disabled={idx === 0} className="text-xs text-slate-400 disabled:opacity-30">↑</button>
                        <button onClick={() => moveFallback(idx, 1)} disabled={idx === cfg.fallback_order.length - 1} className="text-xs text-slate-400 disabled:opacity-30">↓</button>
                        <button onClick={() => setCfg({ ...cfg, fallback_order: cfg.fallback_order.filter((x) => x !== pk) })} className="text-xs text-rose-300">remove</button>
                      </li>
                    ))}
                    {Object.keys(cfg.providers).filter((k) => k !== cfg.primary && !cfg.fallback_order.includes(k)).map((pk) => (
                      <li key={pk}>
                        <button
                          onClick={() => setCfg({ ...cfg, fallback_order: [...cfg.fallback_order, pk] })}
                          className="text-xs text-emerald-300 hover:text-emerald-200"
                        >+ Add {cfg.providers[pk]?.label} as fallback</button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Per-provider cards */}
            {Object.entries(cfg.providers).map(([key, p]) => {
              const isPrimary = cfg.primary === key;
              const inFallback = cfg.fallback_order.includes(key);
              const out = testOut[key];
              return (
                <section key={key} data-testid={`gstin-provider-${key}`}
                  className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-semibold">{p.label}</h3>
                        {isPrimary && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Primary</span>}
                        {inFallback && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-500/30">Fallback</span>}
                        {!isPrimary && !inFallback && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-slate-700 text-slate-300 border border-slate-600">Idle</span>}
                        {p.needs_ip_whitelist && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> IP whitelist
                        </span>}
                      </div>
                      <a href={p.docs} target="_blank" rel="noreferrer" className="text-[11px] text-slate-400 hover:text-slate-200 inline-flex items-center gap-1 mt-0.5">
                        {p.docs} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="text-right text-xs text-slate-400">
                      <div>Today: <span className="font-mono text-slate-200">{p.today_used}</span> / {p.daily_budget || '∞'}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 text-sm text-slate-200">
                      <input
                        type="checkbox"
                        checked={!!p.enabled}
                        onChange={(e) => patchProvider(key, { enabled: e.target.checked })}
                        data-testid={`gstin-${key}-enabled`}
                      />
                      Enabled
                    </label>
                    <div>
                      <Label className="text-slate-300 text-xs uppercase tracking-wider">Daily budget (lookups/day)</Label>
                      <Input
                        type="number" min="0"
                        value={p.daily_budget ?? 1000}
                        onChange={(e) => patchProvider(key, { daily_budget: e.target.value })}
                        data-testid={`gstin-${key}-budget`}
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    {p.cred_fields.map((f) => {
                      const current = p.keys?.[f.key] || '';
                      const isSecret = f.secret;
                      const show = showSecret[`${key}:${f.key}`];
                      const inputType = isSecret && !show ? 'password' : 'text';
                      return (
                        <div key={f.key} className="md:col-span-1">
                          <Label className="text-slate-300 text-xs uppercase tracking-wider">
                            {f.label} {f.optional && <span className="text-slate-500 normal-case text-[10px]">(optional)</span>}
                          </Label>
                          <div className="flex gap-1">
                            <Input
                              type={inputType}
                              value={current}
                              placeholder={f.placeholder}
                              onChange={(e) => patchKey(key, f.key, e.target.value)}
                              data-testid={`gstin-${key}-${f.key}`}
                              className="bg-slate-800 border-slate-700 text-white font-mono text-xs"
                            />
                            {isSecret && (
                              <button
                                type="button"
                                onClick={() => setShowSecret((s) => ({ ...s, [`${key}:${f.key}`]: !show }))}
                                className="px-2 text-slate-400 hover:text-white"
                                title={show ? 'Hide' : 'Reveal'}
                              >
                                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            )}
                          </div>
                          {current === MASK && (
                            <p className="text-[10px] text-slate-500 mt-0.5">Secret stored — clear this field to overwrite, or leave masked to keep.</p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    <Input
                      value={testGstin}
                      onChange={(e) => setTestGstin(e.target.value.toUpperCase())}
                      placeholder="27AABCU9603R1ZK"
                      className="w-56 bg-slate-800 border-slate-700 text-white font-mono text-xs"
                      data-testid={`gstin-${key}-test-input`}
                    />
                    <Button
                      type="button" size="sm"
                      onClick={() => runTest(key)}
                      disabled={!!testing[key]}
                      data-testid={`gstin-${key}-test`}
                      className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold"
                    >
                      {testing[key] ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5 mr-1.5" />}
                      Test lookup
                    </Button>
                    {out && (out.ok ? (
                      <span className="text-xs text-emerald-300 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> {out.data?.name || 'OK'} · {out.data?.city}
                      </span>
                    ) : (
                      <span className="text-xs text-rose-300 flex items-center gap-1 flex-1 min-w-0">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate" title={out.error}>{out.status}: {out.error}</span>
                        {/\b(\d{1,3}\.){3}\d{1,3}\b/.exec(out.error || '') && (
                          <button
                            type="button"
                            onClick={() => {
                              const ip = /\b(\d{1,3}\.){3}\d{1,3}\b/.exec(out.error)[0];
                              navigator.clipboard.writeText(ip);
                              toast.success(`Copied ${ip}`);
                            }}
                            className="text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1 flex-shrink-0"
                          ><Copy className="w-3 h-3" /> Copy IP</button>
                        )}
                      </span>
                    ))}
                  </div>
                </section>
              );
            })}
          </>
        )}
      </main>
    </div>
  );
}

export default AdminGstinProvidersPage;
